import { supabase } from '../supabaseClient';
import { IMAGE_WEBP_MIME } from '../utils/imageCompress';
import { getMemberPhotoSignedUrl } from '../utils/memberPhotoSignedUrlCache';
import type {
  AdminProfileCardSlot,
  ProfileCardRarity,
  WorkspaceProfileCardSet,
  WorkspaceProfileCardSetWithSlots,
} from '../types/profileCard';
import { MAX_WORKSPACE_PROFILE_CARD_SETS, MAX_PROFILE_CARD_SLOTS_PER_SET, parseProfileCardRarity } from '../types/profileCard';
import { recordDevLocalStorageProgramCacheUse } from '../utils/devNetworkTransferMonitor';
import {
  buildProfileCardRowVersion,
  buildWorkspaceSetsCatalogVersion,
  invalidateWorkspaceProfileCardSetsCache,
  loadWorkspaceProfileCardSetsCache,
  saveWorkspaceProfileCardSetsCache,
} from '../utils/profileCardCatalogCache';
import {
  fetchSlideImageStorageUsage,
  notifySlideImageStorageChanged,
} from './slideImageStorageService';

const BUCKET = 'member-profile-images';
const CACHE_CONTROL = '86400';

function isMissingTableError(message: string | undefined): boolean {
  return /does not exist|schema cache/i.test(message ?? '');
}

function bumpWorkspaceSetsCache(workspaceId?: string): void {
  invalidateWorkspaceProfileCardSetsCache(workspaceId);
}

function mapSetRow(row: Record<string, unknown>): WorkspaceProfileCardSet {
  return {
    id: String(row.id ?? ''),
    workspaceId: String(row.workspace_id ?? ''),
    name: typeof row.name === 'string' ? row.name : '',
    sortOrder: Number(row.sort_order) || 0,
    enabled: row.enabled === true,
    priceCoins: Number(row.price_coins) || 0,
    createdAt: typeof row.created_at === 'string' ? row.created_at : '',
    updatedAt: typeof row.updated_at === 'string' ? row.updated_at : '',
  };
}

function mapSlotRow(row: Record<string, unknown>): AdminProfileCardSlot {
  const storagePath = typeof row.storage_path === 'string' ? row.storage_path : null;
  const parsed = parseProfileCardRarity(row.rarity);
  const rarity: ProfileCardRarity | null =
    parsed ?? (storagePath && Number(row.drop_weight) > 0 ? 'normal' : null);
  return {
    id: String(row.id ?? ''),
    setId: String(row.set_id ?? ''),
    slotIndex: Number(row.slot_index) || 0,
    name: typeof row.name === 'string' ? row.name.trim() : '',
    storagePath,
    imageVersion: Number(row.image_version) || 0,
    rarity,
    updatedAt: typeof row.updated_at === 'string' ? row.updated_at : '',
  };
}

function workspaceProfileCardPath(
  userId: string,
  workspaceId: string,
  setId: string,
  slotIndex: number
): string {
  return `${userId}/${workspaceId}/ProfileCardSets/${setId}/${slotIndex}.webp`;
}

/** 사용자 세트 카탈로그 head — 메타만 */
export async function fetchWorkspaceProfileCardSetsHeadVersion(
  workspaceId: string
): Promise<string> {
  if (!workspaceId) return 'empty';
  const { data: setRows, error: setsErr } = await supabase
    .from('workspace_profile_card_sets')
    .select('id, updated_at, enabled, price_coins, sort_order')
    .eq('workspace_id', workspaceId);
  if (setsErr) {
    if (isMissingTableError(setsErr.message)) return 'missing';
    throw new Error(setsErr.message);
  }
  const sets = (setRows ?? []) as Record<string, unknown>[];
  const setIds = sets.map((r) => String(r.id ?? '')).filter(Boolean);
  const setPart = buildProfileCardRowVersion(
    sets.map((r) => ({
      id: String(r.id ?? ''),
      updated_at: String(r.updated_at ?? ''),
      extra: `${r.enabled === true ? '1' : '0'}:${Number(r.price_coins) || 0}:${Number(r.sort_order) || 0}`,
    }))
  );
  if (setIds.length === 0) return `sets:${setPart}||slots:empty`;

  const { data: slotRows, error: slotsErr } = await supabase
    .from('workspace_profile_card_slots')
    .select('id, updated_at, image_version, rarity, storage_path')
    .in('set_id', setIds);
  if (slotsErr && !isMissingTableError(slotsErr.message)) {
    throw new Error(slotsErr.message);
  }
  const slotPart = buildProfileCardRowVersion(
    ((slotRows ?? []) as Record<string, unknown>[]).map((r) => ({
      id: String(r.id ?? ''),
      updated_at: String(r.updated_at ?? ''),
      extra: `${Number(r.image_version) || 0}:${r.rarity ?? ''}:${r.storage_path ? '1' : '0'}`,
    }))
  );
  return `sets:${setPart}||slots:${slotPart}`;
}

/** 사용자 세트 카탈로그 — 네트워크 풀 로드 */
export async function fetchWorkspaceProfileCardSets(
  workspaceId: string
): Promise<WorkspaceProfileCardSetWithSlots[]> {
  if (!workspaceId) return [];
  const [setsRes, slotsRes] = await Promise.all([
    supabase
      .from('workspace_profile_card_sets')
      .select('*')
      .eq('workspace_id', workspaceId)
      .order('sort_order', { ascending: true }),
    supabase.from('workspace_profile_card_slots').select('*').order('slot_index', { ascending: true }),
  ]);
  if (setsRes.error) {
    if (isMissingTableError(setsRes.error.message)) return [];
    throw new Error(setsRes.error.message);
  }
  if (slotsRes.error && !isMissingTableError(slotsRes.error.message)) {
    throw new Error(slotsRes.error.message);
  }
  const setIds = new Set(
    ((setsRes.data ?? []) as Record<string, unknown>[]).map((r) => String(r.id ?? ''))
  );
  const slotsBySet = new Map<string, AdminProfileCardSlot[]>();
  ((slotsRes.data ?? []) as Record<string, unknown>[])
    .map(mapSlotRow)
    .filter((slot) => setIds.has(slot.setId))
    .forEach((slot) => {
      const list = slotsBySet.get(slot.setId) ?? [];
      list.push(slot);
      slotsBySet.set(slot.setId, list);
    });
  return ((setsRes.data ?? []) as Record<string, unknown>[]).map(mapSetRow).map((set) => ({
    ...set,
    slots: slotsBySet.get(set.id) ?? [],
  }));
}

/**
 * 사용자 세트 카탈로그: LS → head 버전 비교 → 동일하면 풀 fetch 생략.
 */
export async function getWorkspaceProfileCardSets(
  workspaceId: string,
  options?: { forceRefresh?: boolean }
): Promise<WorkspaceProfileCardSetWithSlots[]> {
  if (!workspaceId) return [];
  const cached = loadWorkspaceProfileCardSetsCache(workspaceId);
  if (!options?.forceRefresh && cached) {
    try {
      const head = await fetchWorkspaceProfileCardSetsHeadVersion(workspaceId);
      if (head === cached.version) {
        if (process.env.NODE_ENV === 'development') {
          recordDevLocalStorageProgramCacheUse('프로필카드·사용자세트·LS');
        }
        return cached.sets;
      }
    } catch {
      /* fall through */
    }
  }
  const sets = await fetchWorkspaceProfileCardSets(workspaceId);
  saveWorkspaceProfileCardSetsCache(
    workspaceId,
    sets,
    buildWorkspaceSetsCatalogVersion(sets)
  );
  return sets;
}

export async function createWorkspaceProfileCardSet(
  workspaceId: string,
  name: string,
  sortOrder = 0
): Promise<string> {
  const existing = await getWorkspaceProfileCardSets(workspaceId, { forceRefresh: true });
  if (existing.length >= MAX_WORKSPACE_PROFILE_CARD_SETS) {
    throw new Error(`사용자 세트는 최대 ${MAX_WORKSPACE_PROFILE_CARD_SETS}개까지 추가할 수 있습니다.`);
  }
  const { data, error } = await supabase.rpc('create_workspace_profile_card_set', {
    p_workspace_id: workspaceId,
    p_name: name,
    p_sort_order: sortOrder,
  });
  if (error) {
    if (/USER_SET_LIMIT/i.test(error.message ?? '')) {
      throw new Error(`사용자 세트는 최대 ${MAX_WORKSPACE_PROFILE_CARD_SETS}개까지 추가할 수 있습니다.`);
    }
    if (/does not exist|schema cache|Could not find/i.test(error.message ?? '')) {
      // RPC 미적용 폴백
      if (existing.length >= MAX_WORKSPACE_PROFILE_CARD_SETS) {
        throw new Error(`사용자 세트는 최대 ${MAX_WORKSPACE_PROFILE_CARD_SETS}개까지 추가할 수 있습니다.`);
      }
      const { data: inserted, error: insErr } = await supabase
        .from('workspace_profile_card_sets')
        .insert({
          workspace_id: workspaceId,
          name: name.trim(),
          sort_order: sortOrder,
        })
        .select('id')
        .single();
      if (insErr) throw new Error(insErr.message);
      const setId = String(inserted.id);
      const slots = Array.from({ length: 10 }, (_, i) => ({
        set_id: setId,
        slot_index: i + 1,
        drop_weight: 0,
        rarity: null as string | null,
      }));
      const { error: slotErr } = await supabase.from('workspace_profile_card_slots').insert(slots);
      if (slotErr) throw new Error(slotErr.message);
      bumpWorkspaceSetsCache(workspaceId);
      return setId;
    }
    throw new Error(error.message);
  }
  bumpWorkspaceSetsCache(workspaceId);
  return String(data);
}

export async function renameWorkspaceProfileCardSet(setId: string, name: string): Promise<void> {
  const { error } = await supabase
    .from('workspace_profile_card_sets')
    .update({ name: name.trim(), updated_at: new Date().toISOString() })
    .eq('id', setId);
  if (error) throw new Error(error.message);
  bumpWorkspaceSetsCache();
}

export async function updateWorkspaceProfileCardSetMeta(
  setId: string,
  patch: { enabled?: boolean; priceCoins?: number }
): Promise<void> {
  const row: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (typeof patch.enabled === 'boolean') row.enabled = patch.enabled;
  if (typeof patch.priceCoins === 'number') {
    row.price_coins = Math.max(0, Math.round(patch.priceCoins) || 0);
  }
  const { error } = await supabase.from('workspace_profile_card_sets').update(row).eq('id', setId);
  if (error) throw new Error(error.message);
  bumpWorkspaceSetsCache();
}

export async function saveWorkspaceProfileCardSetSettings(
  settings: Array<{ setId: string; enabled: boolean; priceCoins: number }>
): Promise<void> {
  if (settings.length === 0) return;
  const now = new Date().toISOString();
  await Promise.all(
    settings.map((s) =>
      supabase
        .from('workspace_profile_card_sets')
        .update({
          enabled: s.enabled,
          price_coins: Math.max(0, Math.round(s.priceCoins) || 0),
          updated_at: now,
        })
        .eq('id', s.setId)
    )
  ).then((results) => {
    const firstErr = results.find((r) => r.error)?.error;
    if (firstErr) throw new Error(firstErr.message);
  });
  bumpWorkspaceSetsCache();
}

export async function deleteWorkspaceProfileCardSet(setId: string): Promise<void> {
  const { data: slots } = await supabase
    .from('workspace_profile_card_slots')
    .select('storage_path')
    .eq('set_id', setId);
  const paths = ((slots ?? []) as Array<{ storage_path?: string | null }>)
    .map((s) => (s.storage_path ?? '').trim())
    .filter(Boolean);
  for (const path of paths) {
    try {
      await supabase.rpc('release_member_profile_image_asset', { p_path: path });
    } catch {
      /* ignore */
    }
  }
  if (paths.length > 0) {
    await supabase.storage.from(BUCKET).remove(paths);
  }
  await supabase
    .from('class_member_profile_cards')
    .delete()
    .eq('set_id', setId)
    .eq('source_kind', 'workspace');
  const { error } = await supabase.from('workspace_profile_card_sets').delete().eq('id', setId);
  if (error) throw new Error(error.message);
  notifySlideImageStorageChanged();
  bumpWorkspaceSetsCache();
}

export async function addWorkspaceProfileCardSlot(setId: string): Promise<AdminProfileCardSlot> {
  const { data: existingCountRows, error: countErr } = await supabase
    .from('workspace_profile_card_slots')
    .select('slot_index')
    .eq('set_id', setId);
  if (countErr) throw new Error(countErr.message);
  if ((existingCountRows ?? []).length >= MAX_PROFILE_CARD_SLOTS_PER_SET) {
    throw new Error(`프로필 카드는 세트당 최대 ${MAX_PROFILE_CARD_SLOTS_PER_SET}개까지 등록할 수 있습니다.`);
  }

  const { data, error } = await supabase.rpc('add_workspace_profile_card_slot', {
    p_set_id: setId,
  });
  if (!error && data) {
    bumpWorkspaceSetsCache();
    return mapSlotRow(data as Record<string, unknown>);
  }
  if (error && !/does not exist|schema cache|Could not find/i.test(error.message ?? '')) {
    throw new Error(error.message);
  }
  const { data: existing, error: selErr } = await supabase
    .from('workspace_profile_card_slots')
    .select('slot_index')
    .eq('set_id', setId);
  if (selErr) throw new Error(selErr.message);
  if ((existing ?? []).length >= MAX_PROFILE_CARD_SLOTS_PER_SET) {
    throw new Error(`프로필 카드는 세트당 최대 ${MAX_PROFILE_CARD_SLOTS_PER_SET}개까지 등록할 수 있습니다.`);
  }
  const nextIndex =
    Math.max(0, ...((existing ?? []) as Array<{ slot_index?: number }>).map((r) => Number(r.slot_index) || 0)) +
    1;
  if (nextIndex > MAX_PROFILE_CARD_SLOTS_PER_SET) {
    throw new Error(`프로필 카드는 세트당 최대 ${MAX_PROFILE_CARD_SLOTS_PER_SET}개까지 등록할 수 있습니다.`);
  }
  const { data: inserted, error: insErr } = await supabase
    .from('workspace_profile_card_slots')
    .insert({ set_id: setId, slot_index: nextIndex, drop_weight: 0, rarity: null })
    .select('*')
    .single();
  if (insErr) throw new Error(insErr.message);
  bumpWorkspaceSetsCache();
  return mapSlotRow(inserted as Record<string, unknown>);
}

export async function deleteWorkspaceProfileCardSlot(setId: string, slotIndex: number): Promise<void> {
  const { data: slot } = await supabase
    .from('workspace_profile_card_slots')
    .select('storage_path')
    .eq('set_id', setId)
    .eq('slot_index', slotIndex)
    .maybeSingle();
  const path = typeof slot?.storage_path === 'string' ? slot.storage_path : '';
  if (path) {
    try {
      await supabase.rpc('release_member_profile_image_asset', { p_path: path });
    } catch {
      /* ignore */
    }
    await supabase.storage.from(BUCKET).remove([path]);
  }
  await supabase
    .from('class_member_profile_cards')
    .delete()
    .eq('set_id', setId)
    .eq('slot_index', slotIndex)
    .eq('source_kind', 'workspace');
  const { error } = await supabase
    .from('workspace_profile_card_slots')
    .delete()
    .eq('set_id', setId)
    .eq('slot_index', slotIndex);
  if (error) throw new Error(error.message);
  notifySlideImageStorageChanged();
  bumpWorkspaceSetsCache();
}

export async function updateWorkspaceSlotRarity(
  slotId: string,
  rarity: ProfileCardRarity | null,
  name?: string | null
): Promise<void> {
  const trimmed = typeof name === 'string' ? name.trim() : undefined;
  const { error } = await supabase
    .from('workspace_profile_card_slots')
    .update({
      rarity,
      drop_weight: rarity ? 1 : 0,
      ...(trimmed !== undefined ? { name: trimmed || null } : {}),
      updated_at: new Date().toISOString(),
    })
    .eq('id', slotId);
  if (error) throw new Error(error.message);
  bumpWorkspaceSetsCache();
}

/** @deprecated updateWorkspaceSlotRarity 사용 */
export async function updateWorkspaceSlotDropWeight(
  slotId: string,
  dropWeight: number
): Promise<void> {
  await updateWorkspaceSlotRarity(slotId, dropWeight > 0 ? 'normal' : null);
}

export type UploadWorkspaceSlotImageResult = { storagePath: string; imageVersion: number };

export async function uploadWorkspaceProfileCardSlotImage(params: {
  workspaceId: string;
  setId: string;
  slotIndex: number;
  blob: Blob;
  previousPath?: string | null;
}): Promise<UploadWorkspaceSlotImageResult> {
  const { workspaceId, setId, slotIndex, blob, previousPath } = params;
  const { data: userData, error: userErr } = await supabase.auth.getUser();
  const userId = userData.user?.id?.trim();
  if (userErr || !userId) throw new Error('로그인이 필요합니다.');

  const usage = await fetchSlideImageStorageUsage();
  if (usage && usage.bytesUsed + blob.size > usage.bytesLimit) {
    throw new Error('QUOTA_EXCEEDED');
  }

  const storagePath = workspaceProfileCardPath(userId, workspaceId, setId, slotIndex);

  // 이전 경로가 다른 유저 폴더면 먼저 해제·삭제
  const prev = (previousPath ?? '').trim();
  if (prev && prev !== storagePath) {
    try {
      await supabase.rpc('release_member_profile_image_asset', { p_path: prev });
    } catch {
      /* ignore */
    }
    await supabase.storage.from(BUCKET).remove([prev]).catch(() => undefined);
  }

  const { error: uploadErr } = await supabase.storage.from(BUCKET).upload(storagePath, blob, {
    contentType: IMAGE_WEBP_MIME,
    cacheControl: CACHE_CONTROL,
    upsert: true,
  });
  if (uploadErr) throw uploadErr;

  try {
    const { error: regErr } = await supabase.rpc('register_member_profile_image_asset', {
      p_path: storagePath,
      p_bytes: blob.size,
      p_workspace_id: workspaceId,
      p_grade_id: null,
      p_member_id: null,
    });
    if (regErr) {
      if (String(regErr.message ?? '').includes('QUOTA_EXCEEDED')) {
        throw new Error('QUOTA_EXCEEDED');
      }
      throw regErr;
    }
  } catch (e) {
    await supabase.storage.from(BUCKET).remove([storagePath]).catch(() => undefined);
    throw e;
  }

  const imageVersion = Date.now();
  const { error } = await supabase
    .from('workspace_profile_card_slots')
    .update({
      storage_path: storagePath,
      image_version: imageVersion,
      updated_at: new Date().toISOString(),
    })
    .eq('set_id', setId)
    .eq('slot_index', slotIndex);
  if (error) throw new Error(error.message);

  notifySlideImageStorageChanged();
  bumpWorkspaceSetsCache(workspaceId);
  return { storagePath, imageVersion };
}

export async function removeWorkspaceProfileCardSlotImage(
  setId: string,
  slotIndex: number
): Promise<void> {
  const { data: slot } = await supabase
    .from('workspace_profile_card_slots')
    .select('storage_path')
    .eq('set_id', setId)
    .eq('slot_index', slotIndex)
    .maybeSingle();
  const path = typeof slot?.storage_path === 'string' ? slot.storage_path : '';
  if (path) {
    try {
      await supabase.rpc('release_member_profile_image_asset', { p_path: path });
    } catch {
      /* ignore */
    }
    await supabase.storage.from(BUCKET).remove([path]);
  }
  const { error } = await supabase
    .from('workspace_profile_card_slots')
    .update({
      storage_path: null,
      drop_weight: 0,
      rarity: null,
      updated_at: new Date().toISOString(),
    })
    .eq('set_id', setId)
    .eq('slot_index', slotIndex);
  if (error) throw new Error(error.message);
  notifySlideImageStorageChanged();
  bumpWorkspaceSetsCache();
}

/** 사용자 세트 이미지 signed URL (캐시 공유) */
export async function workspaceProfileCardImageUrl(
  storagePath: string | null | undefined,
  imageVersion?: number
): Promise<string | null> {
  return getMemberPhotoSignedUrl(storagePath, imageVersion);
}
